import { useMemo } from 'react';
import { useQuery, keepPreviousData, type QueryClient } from '@tanstack/react-query';
import type { DataResponse, MeScopes, Scope } from '@discovr/types';
import { apiClient } from '../api/apiClient';
import { useAuth } from './AuthContext';
import { profileQueryKey } from './useProfile';

/**
 * useScopes — the caller's authoritative permission set from GET /api/v1/me/scopes
 * (BE: internal/authz/scopes_handler.go). This is the single source of truth for
 * UI gating; it mirrors exactly what the BE enforces at the router. Returns the
 * scopes as a Set for O(1) membership plus the resolved role + brand id.
 *
 * Distinct from useProfile (/profile): scopes are computed from the role for every
 * role here, whereas profile.scopes was only ever populated for admins.
 */
export function useScopes() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: meScopesQueryKey(user?.uid),
    queryFn: () =>
      apiClient
        .get<DataResponse<MeScopes>>('/me/scopes')
        .then((r) => r.data.data),
    enabled: !!user,
    staleTime: 10 * 60_000,
    placeholderData: keepPreviousData,
  });

  const scopes = useMemo(
    () => new Set(query.data?.scopes ?? []),
    [query.data?.scopes],
  );

  return {
    ...query,
    scopes,
    role: query.data?.role,
    brandId: query.data?.brand_id,
  };
}

export function meScopesQueryKey(uid: string | undefined) {
  return ['me-scopes', uid] as const;
}

/** Invalidates both /me/scopes and the profile query for uid — called after a
 * role-change notification/SSE nudge so the caller's UI reflects new
 * permissions without waiting for useScopes's 10-minute staleTime or a
 * full page reload. */
export function invalidateScopesAndProfile(queryClient: QueryClient, uid: string | undefined): void {
  void queryClient.invalidateQueries({ queryKey: meScopesQueryKey(uid) });
  void queryClient.invalidateQueries({ queryKey: profileQueryKey(uid) });
}

/**
 * useCan reports whether the caller holds the given scope(s).
 *   - single scope → must hold it
 *   - array, default → must hold ALL (logical AND)
 *   - array, { anyOf: true } → must hold AT LEAST ONE (logical OR)
 *
 * Returns false while scopes are still loading (fail-closed: hide until known),
 * which matches the BE's deny-by-default posture.
 */
export function useCan(scope: Scope | Scope[], opts?: { anyOf?: boolean }): boolean {
  const { scopes } = useScopes();
  const needed = Array.isArray(scope) ? scope : [scope];
  if (needed.length === 0) return true;
  return opts?.anyOf
    ? needed.some((s) => scopes.has(s))
    : needed.every((s) => scopes.has(s));
}
